/* eslint-disable prefer-object-spread */
import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../../data/axiosInstance/axiosInstance';
import executor from '../../../data/axiosInstance/executor';
import { AllegatiService } from '../../../layouts/tabAllegati/AllegatiService';

const API_URL = `${process.env.REACT_APP_API_SINISTRI_URL}`;

const initialState = {
  allegati: [],
  summaryShortByDocId: {},
  summaryShortStatus: 'idle',
  summaryShortAttemptedClaim: null,
  summaryLongByDocId: {},
  summaryLongStatus: {}
};

export const AllegatiSlice = createSlice({
  name: 'allegati',
  initialState,
  reducers: {
    getAllegati: (state, action) => {
      state.allegati = action.payload;
    },
    setSummaryShortStatus: (state, action) => {
      state.summaryShortStatus = action.payload;
    },
    setSummaryShortMap: (state, action) => {
      state.summaryShortByDocId = action.payload || {};
    },
    setSummaryShortAttemptedClaim: (state, action) => {
      state.summaryShortAttemptedClaim = action.payload;
    },
    setSummaryLongStatus: (state, action) => {
      const { documentId, status } = action.payload;
      state.summaryLongStatus = { ...state.summaryLongStatus, [documentId]: status };
    },
    setSummaryLong: (state, action) => {
      const { documentId, payload } = action.payload;
      state.summaryLongByDocId = { ...state.summaryLongByDocId, [documentId]: payload };
    },
    clearSummaries: (state) => {
      state.summaryShortByDocId = {};
      state.summaryShortStatus = 'idle';
      state.summaryShortAttemptedClaim = null;
      state.summaryLongByDocId = {};
      state.summaryLongStatus = {};
    }
  }
});

export const {
  getAllegati,
  setSummaryShortStatus,
  setSummaryShortMap,
  setSummaryShortAttemptedClaim,
  setSummaryLongStatus,
  setSummaryLong,
  clearSummaries
} = AllegatiSlice.actions;

export const fetchAllegati = (idSinistro) => async (dispatch) => {
  dispatch(getAllegati([]));
  const currentLanguage = localStorage.getItem('lang');
  const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/lista/allegati/${idSinistro}?size=50&page=0`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL, {
    headers: {
      'Language': currentLanguage
    }
  });
  const [res, err] = await executor.executeAsync(fetchData);

  console.log('res', res);
  console.log('err', err);

  if (res?.data) {
    return dispatch(getAllegati(res.data.content));
  }
  return dispatch(getAllegati([]));
};

/**
 * Fetch the short-summary list from VERO and index it by document_id.
 * Failures degrade silently: the table still renders without abstracts.
 *
 * Re-entrancy + retry-on-error guards:
 *   - skip if a fetch is already in flight for any claim;
 *   - skip if the same claimNumber already succeeded or errored;
 *     a different claimNumber (or `clearSummaries`) is required to retry.
 *   This prevents the API from being hit in a loop when the endpoint fails
 *   and an upstream re-render keeps re-triggering the effect.
 */
export const fetchAllegatiShortSummary = (claimNumber) => (dispatch, getState) => {
  if (!claimNumber) {
    dispatch(setSummaryShortMap({}));
    dispatch(setSummaryShortStatus('idle'));
    dispatch(setSummaryShortAttemptedClaim(null));
    return;
  }

  const slice = getState().allegatiReducer || {};
  if (slice.summaryShortStatus === 'loading') return;
  if (
    (slice.summaryShortStatus === 'error' || slice.summaryShortStatus === 'succeeded') &&
    slice.summaryShortAttemptedClaim === claimNumber
  ) {
    return;
  }

  dispatch(setSummaryShortAttemptedClaim(claimNumber));
  dispatch(setSummaryShortStatus('loading'));

  AllegatiService.getClaimFilesShortSummary(claimNumber)
    .then((res) => {
      if (!res?.data?.files) {
        console.warn('[Allegati] short summary fetch failed');
        dispatch(setSummaryShortMap({}));
        dispatch(setSummaryShortStatus('error'));
        return;
      }
      const map = {};
      res.data.files.forEach((f) => {
        if (f?.document_id) {
          map[f.document_id] = f;
        }
      });
      dispatch(setSummaryShortMap(map));
      dispatch(setSummaryShortStatus('succeeded'));
    })
    .catch((err) => {
      console.warn('[Allegati] short summary fetch failed', err);
      dispatch(setSummaryShortMap({}));
      dispatch(setSummaryShortStatus('error'));
    });
};

/**
 * Fetch the long-summary for a single document. Cached by documentId.
 *
 * Skips when the document already succeeded, errored or is currently loading
 * — manual retry requires `clearSummaries` (e.g. closing/reopening the panel).
 */
export const fetchAllegatoLongSummary = (claimNumber, documentId) => (dispatch, getState) => {
  if (!claimNumber || !documentId) return;
  const slice = getState().allegatiReducer || {};
  if (slice.summaryLongByDocId?.[documentId]) return;
  const status = slice.summaryLongStatus?.[documentId];
  if (status === 'loading' || status === 'error') return;

  dispatch(setSummaryLongStatus({ documentId, status: 'loading' }));

  AllegatiService.getClaimFileLongSummary(claimNumber, documentId)
    .then((res) => {
      dispatch(setSummaryLong({ documentId, payload: res.data }));
      dispatch(setSummaryLongStatus({ documentId, status: 'succeeded' }));
    })
    .catch((err) => {
      console.warn('[Allegati] long summary fetch failed', err);
      dispatch(setSummaryLongStatus({ documentId, status: 'error' }));
    });
};

export default AllegatiSlice.reducer;
